import { DrawerContentComponentProps, DrawerContentOptions } from "@react-navigation/drawer";
import React from "react";
import { ScrollView, StyleProp, StyleSheet, TouchableOpacity, View, ViewStyle } from "react-native";
import { useSelector } from "react-redux";
import { Color } from "react-native-svg";
import { DrawerNavigationHelpers } from "@react-navigation/drawer/lib/typescript/src/types";

import * as orderDb from "src/db/order.db";
import * as connectionDb from "src/db/connection.db";
import { kStyleConst } from "src/kStyle";
import { kColors } from "src/kColors";
import { kRoute } from "src/kRoute";
import {
  Box,
  Link,
  MoneyDollarCircle,
  PdChevronDown,
  PdChevronUp,
  PdCurrencyDollar,
  PdExit,
  PdHome,
  PdMessage,
  PdSettings,
  PdShoppingBag,
  Shapes,
  User,
} from "../Icons";
import { PdBadge, PdBadgeLabel, PdText } from "../layout";
import { TermsApplicationDetails } from "../connected";
import { useUserProfile } from "src/hooks/ui";
import { postFeatureUsed } from "src/util/apiNew";
import { SLImage } from "..";
import { useAppDispatch } from "src/redux";
import { logout } from "src/redux/user/actions";

interface MenuContextInterface {
  currentRouteName: string;
  isMenuOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
}

const MenuContext = React.createContext<Partial<MenuContextInterface>>({});

const Menu: React.FC<{ activeRoutes?: string[]; style?: StyleProp<ViewStyle> }> = ({
  activeRoutes,
  children,
  style,
}) => {
  const [isMenuOpen, setMenuOpen] = React.useState(false);
  const rCurrentRouteName = useSelector((state) => state.NavigationReducer.currentRouteName);

  const isActive = activeRoutes?.includes(rCurrentRouteName || "");

  const openMenu = () => {
    setMenuOpen(true);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <MenuContext.Provider value={{ currentRouteName: rCurrentRouteName, isMenuOpen, openMenu, closeMenu }}>
      <View style={[isActive ? styles.menuActive : styles.menu, style]}>{children}</View>
    </MenuContext.Provider>
  );
};

const MenuButton: React.FC<{
  navigation: DrawerNavigationHelpers;
  leftIcon?: React.ReactElement<{ color?: Color }>;
  rightIcon?: React.ReactElement;
  label: string;
  to?: string;
  onPress?: () => void;
}> = ({ navigation, leftIcon, rightIcon, label, to, onPress }) => {
  const { isMenuOpen, openMenu } = React.useContext(MenuContext);

  const handleOnPress = () => {
    onPress?.();
    if (to) {
      navigation.navigate(to);
    }

    if (!isMenuOpen) {
      openMenu?.();
    }
  };

  return (
    <TouchableOpacity onPress={handleOnPress} style={styles.menuButton}>
      {leftIcon && <View style={styles.menuButtonLeftIcon}>{leftIcon}</View>}
      <PdText style={styles.menuButtonLabel}>{label}</PdText>
      {rightIcon}
    </TouchableOpacity>
  );
};

const MenuList: React.FC = ({ children }) => {
  const { isMenuOpen } = React.useContext(MenuContext);

  if (!isMenuOpen) {
    return null;
  }

  return <View style={styles.menuList}>{children}</View>;
};

const MenuItem: React.FC<{
  navigation: DrawerNavigationHelpers;
  label: string;
  to?: string;
}> = ({ navigation, label, to }) => {
  const { currentRouteName } = React.useContext(MenuContext);

  const isActive = currentRouteName === to;

  const onPress = () => {
    if (to) {
      navigation.navigate(to);
    }
  };

  return (
    <TouchableOpacity onPress={onPress} style={styles.menuItem}>
      <PdText style={isActive ? styles.menuItemLabelActive : styles.menuItemLabel}>{label}</PdText>
    </TouchableOpacity>
  );
};

const MenuChevronButton: React.FC = () => {
  const { isMenuOpen, openMenu, closeMenu } = React.useContext(MenuContext);

  const onPress = () => {
    if (!isMenuOpen) {
      openMenu?.();
    } else {
      closeMenu?.();
    }
  };

  const SvgIconComp = isMenuOpen ? PdChevronUp : PdChevronDown;

  return (
    <TouchableOpacity onPress={onPress} style={styles.menuChevronButton}>
      <SvgIconComp style={styles.menuChevronButtonIcon} />
    </TouchableOpacity>
  );
};

const UnreadThreadsBadge: React.FC = () => {
  const rUnreadThreadsQuantity = useSelector(
    (state) =>
      Object.values(state.MessageThreadsReducer.rThreads).filter(
        (thread) => thread.isUnread && thread.recipientStr !== "The Main Tab | Activity",
      ).length,
  );

  if (!rUnreadThreadsQuantity) {
    return null;
  }

  return (
    <View style={styles.badge}>
      <PdText variant="body-sm" style={styles.badgeLabel}>
        {rUnreadThreadsQuantity}
      </PdText>
    </View>
  );
};

const PendingOrdersBadge: React.FC = () => {
  const [pendingOrdersQuantity, setPendingOrdersQuantity] = React.useState(0);

  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const { isBrand } = useUserProfile();

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    if (isBrand) {
      unsubscribe = orderDb
        .queryOrder({ brandId: rCompanyId, status: ["Pending", "Awaiting Fulfillment"] })
        .onSnapshot((snapshot) => {
          setPendingOrdersQuantity(snapshot.size);
        });
    }

    return () => {
      unsubscribe?.();
    };
  }, [isBrand, rCompanyId]);

  if (!pendingOrdersQuantity || !isBrand) {
    return null;
  }

  return (
    <View style={styles.badge}>
      <PdText variant="body-xs" style={styles.badgeLabel}>
        {pendingOrdersQuantity}
      </PdText>
    </View>
  );
};

const PendingConnectionsBadge: React.FC = () => {
  const [pendingConnectionsQuantity, setPendingConnectionsQuantity] = React.useState(0);
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rAccountType = useSelector((state) => state.UserReducer.profile.accountType);

  React.useEffect(() => {
    const params: connectionDb.tQueryBrandRetailerConnectionsParams =
      rAccountType === "tmtAdmin"
        ? { status: "Request" }
        : { status: ["RequestToRetailer", "RequestToBrand", "Request"] };
    if (rAccountType === "brand") params.brandId = rCompanyId;
    if (rAccountType === "retailer") params.retailerId = rCompanyId;
    if (rAccountType === "rep") params.repId = rCompanyId;

    const unsubscribe = connectionDb.queryBrandRetailerConnection(params).onSnapshot((snapshot) => {
      setPendingConnectionsQuantity(snapshot.size);
    });

    return () => {
      unsubscribe();
    };
  }, [rAccountType, rCompanyId]);

  if (!pendingConnectionsQuantity) {
    return null;
  }

  return (
    <View style={styles.badge}>
      <PdText variant="body-xs" style={styles.badgeLabel}>
        {pendingConnectionsQuantity}
      </PdText>
    </View>
  );
};

const DrawerContent: React.FC<DrawerContentComponentProps<DrawerContentOptions>> = ({ navigation }) => {
  const rProfile = useSelector(({ UserReducer }) => UserReducer.profile);
  const rProfileImageUrl = useSelector((state) =>
    state.UserReducer.profile.accountType === "tmtAdmin"
      ? state.UserReducer.profile.avatar
      : state.CompanyReducer.type === "brand"
      ? state.CompanyReducer.images.logoDownloadURL
      : state.CompanyReducer.type === "retailer" || state.CompanyReducer.type === "rep"
      ? state.CompanyReducer.avatar
      : "",
  );
  const rCompanyId = useSelector((s) =>
    s.CompanyReducer.type === "retailer" || s.CompanyReducer.type === "brand" || s.CompanyReducer.type === "rep"
      ? s.CompanyReducer.id
      : "",
  );
  const rRetailerFeaturesUsed = useSelector((s) =>
    s.CompanyReducer.type === "retailer" ? s.CompanyReducer.featuresUsed ?? [] : [],
  );
  const rIsShopifyConnected = useSelector(
    (s) => s.CompanyReducer.type === "brand" && !!s.CompanyReducer.shopifySessionId,
  );
  const dispatch = useAppDispatch();
  const isUserAdmin = rProfile.accountType === "tmtAdmin";
  const isUserRetailer = rProfile.accountType === "retailer";
  const isUserBrand = rProfile.accountType === "brand";
  const isUserSalesRep = rProfile.accountType === "rep";

  const _navToAccount = () => {
    navigation.navigate(kRoute.P09);
  };

  const handleOnPressReferralsRewardsMenu = () => {
    if (rCompanyId && !rRetailerFeaturesUsed?.includes("referrals-rewards")) {
      postFeatureUsed(rCompanyId, "referrals-rewards");
    }
  };

  const _handleLogOut = () => {
    dispatch(logout());
  };

  return (
    <ScrollView nativeID="management-drawer" style={styles.container} contentContainerStyle={styles.content}>
      <TermsApplicationDetails colorScheme="white" style={styles.creditLimit} />
      <View>
        {isUserRetailer ? (
          <Menu activeRoutes={[kRoute.P99]}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P99}
              leftIcon={<PdShoppingBag color={kColors.grayPink} />}
              label="Shop!"
            />
          </Menu>
        ) : (
          <Menu activeRoutes={[kRoute.P02]}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P02}
              leftIcon={<PdHome color={kColors.grayPink} />}
              label="Home"
            />
          </Menu>
        )}
        <Menu activeRoutes={[kRoute.P03, kRoute.P03_01, kRoute.P03_02, kRoute.P03_03, kRoute.P03_04]}>
          <View style={styles.menuButtonRow}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P03}
              leftIcon={<Box color={kColors.grayPink} />}
              rightIcon={<PendingOrdersBadge />}
              label="Orders"
            />
            {!isUserRetailer && <MenuChevronButton />}
          </View>
          {!isUserRetailer && (
            <MenuList>
              <MenuItem navigation={navigation} to={kRoute.P03_02} label="Created Orders" />
              {isUserBrand && <MenuItem navigation={navigation} to={kRoute.P03_03} label="Abandoned Carts" />}
              {!isUserAdmin && <MenuItem navigation={navigation} to={kRoute.P03_04} label="Payouts" />}
            </MenuList>
          )}
        </Menu>
        <Menu activeRoutes={[kRoute.P04, kRoute.P04_01]}>
          <MenuButton
            navigation={navigation}
            to={kRoute.P04}
            leftIcon={<PdMessage color={kColors.grayPink} />}
            rightIcon={<UnreadThreadsBadge />}
            label="Messages"
          />
        </Menu>
        {!isUserRetailer && (
          <Menu
            activeRoutes={[
              kRoute.P06,
              kRoute.P06_05,
              kRoute.P06_09,
              kRoute.P06_10,
              kRoute.P06_11,
              kRoute.P12,
              kRoute.P13,
              kRoute.P14,
              kRoute.P20,
            ]}
          >
            <View style={styles.menuButtonRow}>
              <MenuButton
                navigation={navigation}
                to={kRoute.P06}
                leftIcon={<Link color={kColors.grayPink} />}
                rightIcon={<PendingConnectionsBadge />}
                label={isUserBrand ? "My Customers" : isUserSalesRep ? "My Connections" : "Connections"}
              />
              {(isUserAdmin || isUserBrand) && <MenuChevronButton />}
            </View>
            {isUserAdmin && (
              <MenuList>
                <MenuItem navigation={navigation} to={kRoute.P13} label="Applications" />
                <MenuItem navigation={navigation} to={kRoute.P14} label="Customers" />
              </MenuList>
            )}
            {isUserBrand && (
              <MenuList>
                <MenuItem navigation={navigation} to={kRoute.P12} label="Sales Reps" />
              </MenuList>
            )}
          </Menu>
        )}
        {isUserRetailer && (
          <Menu activeRoutes={[kRoute.P21]}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P21}
              leftIcon={<Link color={kColors.grayPink} />}
              label="My Brands"
            />
          </Menu>
        )}
        {isUserBrand && (
          <Menu activeRoutes={[kRoute.P08, kRoute.P08_01, kRoute.P08_02]}>
            <View style={styles.menuButtonRow}>
              <MenuButton
                navigation={navigation}
                to={kRoute.P08}
                leftIcon={<Shapes color={kColors.grayPink} />}
                label="Products"
              />
              {rIsShopifyConnected && <MenuChevronButton />}
            </View>
            {rIsShopifyConnected && (
              <MenuList>
                <MenuItem navigation={navigation} to={kRoute.P08_02} label="Shopify Integration" />
              </MenuList>
            )}
          </Menu>
        )}
        {isUserBrand && (
          <Menu activeRoutes={[kRoute.P05]}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P05}
              leftIcon={<MoneyDollarCircle color={kColors.grayPink} />}
              label="Promotions"
            />
          </Menu>
        )}
        {/* {isUserRetailer && (
          <Menu activeRoutes={[kRoute.P15]}>
            <MenuButton
              to={kRoute.P15}
              leftIcon={<Bookmark color={kColors.grayPink} />}
              label="Saved Brands"
            />
          </Menu>
        )} */}
        {isUserSalesRep && (
          <Menu activeRoutes={[kRoute.P17]}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P17}
              leftIcon={<User color={kColors.grayPink} />}
              label="My Brands"
            />
          </Menu>
        )}
        {isUserSalesRep && (
          <Menu activeRoutes={[kRoute.P18]}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P18}
              leftIcon={<PdShoppingBag color={kColors.grayPink} />}
              label="My Retailers"
            />
          </Menu>
        )}
        {!isUserAdmin && (
          <Menu activeRoutes={[kRoute.P09]}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P09}
              leftIcon={<PdSettings color={kColors.grayPink} />}
              label="Settings"
            />
          </Menu>
        )}
        {isUserAdmin && (
          <Menu activeRoutes={[kRoute.P11]}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P11}
              leftIcon={<PdSettings color={kColors.grayPink} />}
              label="Reports"
            />
          </Menu>
        )}
        {isUserAdmin && (
          <Menu activeRoutes={[kRoute.P16, kRoute.P16_01]}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P16}
              leftIcon={<PdSettings color={kColors.grayPink} />}
              label="Transactions"
            />
          </Menu>
        )}
        {isUserRetailer && (
          <Menu activeRoutes={[kRoute.P19]}>
            <MenuButton
              navigation={navigation}
              to={kRoute.P19}
              onPress={handleOnPressReferralsRewardsMenu}
              leftIcon={<PdCurrencyDollar color={kColors.grayPink} />}
              rightIcon={
                !rRetailerFeaturesUsed.includes("referrals-rewards") ? (
                  <PdBadge style={{ backgroundColor: kColors.brandGreen }}>
                    <PdBadgeLabel style={{ color: kColors.white }}>NEW</PdBadgeLabel>
                  </PdBadge>
                ) : (
                  <></>
                )
              }
              label="Referrals & Rewards"
            />
          </Menu>
        )}
      </View>
      <Menu style={styles.menuButtonLogout}>
        <MenuButton
          onPress={_handleLogOut}
          navigation={navigation}
          leftIcon={<PdExit color={kColors.grayPink} />}
          label="Logout"
        />
      </Menu>
      <View style={styles.profile}>
        <SLImage
          source={{ uri: rProfileImageUrl }}
          mobileStyle={styles.profileImage}
          webStyle={webStyles.mDrawerProfile}
        />
        <TouchableOpacity style={styles.profileName} onPress={_navToAccount}>
          <PdText style={styles.profileNameLabel} numberOfLines={1}>
            {rProfile.firstName}
          </PdText>
          <PdText style={styles.profileNameLabel} numberOfLines={1}>
            {rProfile.lastName}
          </PdText>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

export default React.memo(DrawerContent);

const styles = StyleSheet.create({
  badge: {
    alignItems: "center",
    backgroundColor: kColors.brandCoral,
    borderRadius: 999,
    height: 19,
    justifyContent: "center",
    marginLeft: 2 * kStyleConst.kSpacing,
    width: 19,
  },
  badgeLabel: {
    color: kColors.white,
  },
  container: {
    backgroundColor: kColors.brandGreen400,
  },
  content: {
    flex: 1,
    paddingBottom: 8 * kStyleConst.kSpacing,
    paddingHorizontal: 4 * kStyleConst.kSpacing,
    paddingTop: 8 * kStyleConst.kSpacing,
  },
  creditLimit: {
    marginBottom: 6 * kStyleConst.kSpacing,
    marginTop: 4 * kStyleConst.kSpacing,
    paddingHorizontal: 3 * kStyleConst.kSpacing,
  },
  menu: {
    borderRadius: 8,
    marginBottom: 2 * kStyleConst.kSpacing,
    paddingHorizontal: 3 * kStyleConst.kSpacing,
  },
  menuActive: {
    backgroundColor: kColors.brandGreen,
    borderRadius: 8,
    marginBottom: 2 * kStyleConst.kSpacing,
    paddingHorizontal: 3 * kStyleConst.kSpacing,
  },
  menuButton: {
    alignItems: "center",
    flexDirection: "row",
    flex: 1,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  menuButtonLabel: {
    color: kColors.white,
  },
  menuButtonLeftIcon: {
    height: 20,
    marginRight: 2 * kStyleConst.kSpacing,
    width: 20,
  },
  menuButtonLogout: {
    marginBottom: 4 * kStyleConst.kSpacing,
    marginTop: "auto",
  },
  menuButtonRow: {
    alignItems: "center",
    flexDirection: "row",
  },
  menuChevronButton: {
    height: "100%",
    justifyContent: "center",
    paddingLeft: 2 * kStyleConst.kSpacing,
  },
  menuChevronButtonIcon: {
    color: kColors.white,
    height: 8,
    width: 8,
  },
  menuItem: {
    alignItems: "center",
    flexDirection: "row",
    paddingLeft: 28,
    paddingVertical: 1 * kStyleConst.kSpacing,
  },
  menuItemLabel: {
    color: kColors.gray600,
    fontWeight: "600",
  },
  menuItemLabelActive: {
    color: kColors.white,
    fontWeight: "600",
  },
  menuList: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  profile: {
    alignItems: "center",
    flexDirection: "row",
  },
  profileImage: {
    backgroundColor: kColors.white,
    borderRadius: 999,
    borderWidth: 0,
    height: 50,
    marginRight: 4 * kStyleConst.kSpacing,
    width: 50,
  },
  profileName: {
    height: 50,
    justifyContent: "center",
  },
  profileNameLabel: {
    color: kColors.white,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  profileImage: {
    backgroundColor: kColors.white,
    borderRadius: 999,
    borderWidth: 0,
    height: 50,
    marginRight: 4 * kStyleConst.kSpacing,
    width: 50,
  },
} as const;
